import { useAtom } from "jotai";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import AppleIcon from "@mui/icons-material/Apple";
import {
  AppBar,
  AppBarProps,
  Box,
  Button,
  ButtonBase,
  Chip,
  Container,
  Fade,
  Grid,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";

import ICON from "@/assets/icons/icon.png";
import { AccountJobsCompose, AccountMenu } from "@/components/account";
import { IOS_APP_STORE_URL } from "@/config";
import { useAuth } from "@/providers";
import { AppRoutes } from "@/routes";
import { StoreTitle } from "@/store";

import { layoutClasses } from "./Layout";

export interface HeadProps extends AppBarProps {}

export const Head: React.FC<HeadProps> = ({ sx, ...props }) => {
  const { transitions } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [storeTitle] = useAtom(StoreTitle);

  const handleScrollOnTap = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <AppBar
      className={layoutClasses.head}
      component="header"
      onClick={handleScrollOnTap}
      position="fixed"
      sx={{
        ...sx,
        displayPrint: "none",
        paddingTop: `env(safe-area-inset-top)`,
        transition: transitions.create(["all"], {
          duration: transitions.duration.standard,
        }),
      }}
      {...props}
    >
      <Toolbar disableGutters>
        <Container maxWidth="xl">
          <Grid container alignItems="center" spacing={0}>
            <Grid size={{ xs: "grow" }}>
              <Stack alignItems="center" direction="row" spacing={0.5}>
                <ButtonBase
                  component={RouterLink}
                  to={user ? AppRoutes.HOME() : AppRoutes.ROOT()}
                  sx={{
                    borderRadius: 0.5,
                    py: 0.75,
                    px: 1,
                  }}
                >
                  <Box
                    alt="Baiki"
                    component="img"
                    src={ICON}
                    sx={{
                      display: "block",
                      height: { xs: 16, md: 20, lg: 24 },
                    }}
                  />
                </ButtonBase>
                {import.meta.env.VITE_APP_ENV === "development" ? (
                  <Chip
                    color="primary"
                    label="⍺"
                    size="small"
                    variant="outlined"
                    sx={{
                      fontWeight: "400",
                      display: "inline-block",
                      lineHeight: "1.66em",
                    }}
                  />
                ) : null}
              </Stack>
            </Grid>
            {user ? (
              <Grid size={{ xs: 7 }}>
                {storeTitle && (
                  <Fade in key={storeTitle}>
                    <Typography
                      align="center"
                      color="text.primary"
                      fontWeight={600}
                      variant="subtitle2"
                    >
                      {storeTitle}
                    </Typography>
                  </Fade>
                )}
              </Grid>
            ) : null}
            <Grid size={{ xs: "grow" }} textAlign="right">
              {user ? (
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                >
                  <AccountJobsCompose />
                  <AccountMenu />
                </Stack>
              ) : (
                <Button
                  component="a"
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  startIcon={<AppleIcon />}
                  sx={{ mr: -1 }}
                  variant="text"
                >
                  {t("common.downloadIosApp")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Container>
      </Toolbar>
    </AppBar>
  );
};
